import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, ArrowRight, AlertTriangle, CheckCircle2, Award, Layers3 } from "lucide-react";
import { Bar, Doughnut } from "react-chartjs-2";
import {
    Chart as ChartJS,
    ArcElement,
    BarElement,
    CategoryScale,
    LinearScale,
    Legend,
    Tooltip,
} from "chart.js";

import { Button } from "../ui/button";
import StatusPill from "./StatusPill";
import { cn } from "../../utils/tailwindUtils";

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Legend, Tooltip);

const STATUS_BUCKETS = [
    { key: "draft", label: "Draft", color: "rgba(148,163,184,0.6)" },
    { key: "pending_employee_submission", label: "Pending Employee", color: "rgba(245,158,11,0.75)" },
    { key: "in_progress", label: "In Progress", color: "rgba(59,130,246,0.75)" },
    { key: "submitted_to_rm", label: "With RM", color: "rgba(99,102,241,0.75)" },
    { key: "sent_back_by_rm", label: "Sent Back (RM)", color: "rgba(249,115,22,0.75)" },
    { key: "rm_reviewed", label: "RM Reviewed", color: "rgba(14,165,233,0.75)" },
    { key: "pending_hr_approval", label: "Pending HR", color: "rgba(139,92,246,0.85)" },
    { key: "sent_back_by_hr", label: "Sent Back (HR)", color: "rgba(244,63,94,0.75)" },
    { key: "hr_approved", label: "HR Approved", color: "rgba(34,197,94,0.75)" },
    { key: "published", label: "Published", color: "rgba(16,185,129,0.85)" },
    { key: "closed", label: "Closed", color: "rgba(100,116,139,0.6)" },
    { key: "overdue", label: "Overdue", color: "rgba(239,68,68,0.85)" },
];

const RATING_BANDS = [
    { label: "Outstanding", min: 4.5, color: "rgba(16,185,129,0.8)" },
    { label: "Exceeds", min: 3.5, color: "rgba(14,165,233,0.8)" },
    { label: "Meets", min: 2.5, color: "rgba(245,158,11,0.8)" },
    { label: "Needs Improvement", min: 1.5, color: "rgba(249,115,22,0.8)" },
    { label: "Poor", min: 0, color: "rgba(239,68,68,0.8)" },
];

const bandOf = (score) => {
    if (score === null || score === undefined) return null;
    const s = Number(score);
    for (const b of RATING_BANDS) if (s >= b.min) return b.label;
    return "Poor";
};

const SmallStat = ({ icon: Icon, label, value, accent }) => (
    <div className="flex items-center gap-2 rounded-md border p-2.5 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
        <div className={cn("p-1.5 rounded-md flex-shrink-0", accent)}>
            <Icon className="w-4 h-4" />
        </div>
        <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
            <p className="text-base font-semibold text-gray-900 dark:text-gray-100 leading-tight">{value}</p>
        </div>
    </div>
);

const HRGovernanceSummary = ({ appraisals = [] }) => {
    const navigate = useNavigate();

    const counts = useMemo(() => {
        const c = Object.fromEntries(STATUS_BUCKETS.map((b) => [b.key, 0]));
        for (const a of appraisals) {
            if (c[a.status] !== undefined) c[a.status] += 1;
        }
        return c;
    }, [appraisals]);

    const total = appraisals.length;
    const pendingHR = counts.pending_hr_approval;
    const published = counts.published;
    const overdue = counts.overdue;

    // Donut — overall org-wide status distribution.
    const donutData = useMemo(() => {
        const present = STATUS_BUCKETS.filter((b) => counts[b.key] > 0);
        return {
            labels: present.map((b) => b.label),
            datasets: [{
                data: present.map((b) => counts[b.key]),
                backgroundColor: present.map((b) => b.color),
                borderWidth: 1,
            }],
        };
    }, [counts]);

    const donutOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        cutout: "55%",
        plugins: {
            legend: { position: "bottom", labels: { boxWidth: 10, font: { size: 11 } } },
            tooltip: { callbacks: { label: (ctx) => ` ${ctx.label}: ${ctx.parsed}` } },
        },
    }), []);

    // Department-wise completion: stacked Published vs Pending.
    const departmentStats = useMemo(() => {
        const m = new Map();
        for (const a of appraisals) {
            const dept = a.employee?.department || "Unassigned";
            if (!m.has(dept)) m.set(dept, { published: 0, in_flight: 0, pending_hr: 0 });
            const row = m.get(dept);
            if (a.status === "published") row.published += 1;
            else if (a.status === "pending_hr_approval") row.pending_hr += 1;
            else row.in_flight += 1;
        }
        return [...m.entries()].map(([dept, v]) => ({ dept, ...v }));
    }, [appraisals]);

    const deptBarData = useMemo(() => ({
        labels: departmentStats.map((d) => d.dept),
        datasets: [
            { label: "Published", data: departmentStats.map((d) => d.published), backgroundColor: "rgba(16,185,129,0.75)", borderRadius: 3 },
            { label: "Pending HR", data: departmentStats.map((d) => d.pending_hr), backgroundColor: "rgba(139,92,246,0.75)", borderRadius: 3 },
            { label: "In Flight", data: departmentStats.map((d) => d.in_flight), backgroundColor: "rgba(59,130,246,0.55)", borderRadius: 3 },
        ],
    }), [departmentStats]);

    const deptBarOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: "bottom", labels: { boxWidth: 10, font: { size: 11 } } } },
        scales: {
            x: { stacked: true, ticks: { font: { size: 11 } }, grid: { display: false } },
            y: { stacked: true, beginAtZero: true, ticks: { stepSize: 1, font: { size: 11 } }, grid: { color: "rgba(148,163,184,0.2)" } },
        },
    }), []);

    // Rating distribution across published appraisals.
    const ratingDistribution = useMemo(() => {
        const m = Object.fromEntries(RATING_BANDS.map((b) => [b.label, 0]));
        for (const a of appraisals) {
            const b = bandOf(a.final_score);
            if (b && m[b] !== undefined) m[b] += 1;
        }
        return m;
    }, [appraisals]);

    const ratingData = useMemo(() => ({
        labels: RATING_BANDS.map((b) => b.label),
        datasets: [{
            label: "Employees",
            data: RATING_BANDS.map((b) => ratingDistribution[b.label]),
            backgroundColor: RATING_BANDS.map((b) => b.color),
            borderRadius: 4,
        }],
    }), [ratingDistribution]);

    const ratingOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
            x: { ticks: { font: { size: 11 } }, grid: { display: false } },
            y: { beginAtZero: true, ticks: { stepSize: 1, font: { size: 11 } }, grid: { color: "rgba(148,163,184,0.2)" } },
        },
    }), []);

    const pendingHRList = useMemo(() => {
        return appraisals
            .filter((a) => a.status === "pending_hr_approval")
            .slice(0, 5);
    }, [appraisals]);

    const hasRatings = Object.values(ratingDistribution).some((v) => v > 0);

    return (
        <div className="space-y-4">
            {/* Header card with global stats */}
            <div className="rounded-lg border border-violet-200 dark:border-violet-900/40 bg-gradient-to-r from-violet-50 to-white dark:from-violet-900/15 dark:to-gray-900 p-4 sm:p-5">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-md bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">
                            <Building2 className="w-5 h-5" />
                        </div>
                        <div>
                            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Organization Overview</h3>
                            <p className="text-xs text-muted-foreground">Appraisal status across the company</p>
                        </div>
                    </div>
                    <Button
                        size="sm"
                        variant="brand"
                        onClick={() => navigate("/appraisal/final-approval")}
                    >
                        Open Final Approval <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
                    <SmallStat icon={Layers3} label="Total Appraisals" value={total} accent="bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300" />
                    <SmallStat icon={AlertTriangle} label="Pending HR" value={pendingHR} accent="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" />
                    <SmallStat icon={Award} label="Published" value={published} accent="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" />
                    <SmallStat icon={AlertTriangle} label="Overdue" value={overdue} accent="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300" />
                </div>
            </div>

            {/* Status distribution + Pending HR list */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-5">
                    <h3 className="text-base font-semibold mb-2 text-gray-900 dark:text-gray-100">Status Distribution</h3>
                    {total === 0 ? (
                        <div className="text-sm text-muted-foreground py-8 text-center">No appraisals yet.</div>
                    ) : (
                        <div className="h-64">
                            <Doughnut data={donutData} options={donutOptions} />
                        </div>
                    )}
                </div>

                <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-5">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Pending HR Approval</h3>
                        {pendingHRList.length > 0 && (
                            <Button variant="ghost" size="sm" onClick={() => navigate("/appraisal/final-approval")}>
                                View all <ArrowRight className="w-4 h-4 ml-1" />
                            </Button>
                        )}
                    </div>
                    {pendingHRList.length === 0 ? (
                        <div className="text-sm text-muted-foreground py-8 text-center">
                            <CheckCircle2 className="w-7 h-7 mx-auto mb-2 text-emerald-400" />
                            No appraisals waiting on your approval.
                        </div>
                    ) : (
                        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                            {pendingHRList.map((a) => (
                                <li
                                    key={a.id}
                                    onClick={() => navigate(`/appraisal/final-approval/${a.id}`)}
                                    className="flex items-center justify-between gap-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40 px-2 -mx-2 rounded-md transition-colors"
                                >
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                            {a.employee?.name || "Employee"}
                                        </p>
                                        <p className="text-[11px] text-muted-foreground truncate">
                                            {a.employee?.designation}{a.employee?.department ? ` · ${a.employee.department}` : ""}
                                        </p>
                                    </div>
                                    <StatusPill value={a.status} />
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            {/* Department breakdown + Rating distribution */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-5">
                    <h3 className="text-base font-semibold mb-2 text-gray-900 dark:text-gray-100">Department Progress</h3>
                    {departmentStats.length === 0 ? (
                        <div className="text-sm text-muted-foreground py-8 text-center">No data yet.</div>
                    ) : (
                        <div className="h-64">
                            <Bar data={deptBarData} options={deptBarOptions} />
                        </div>
                    )}
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-5">
                    <h3 className="text-base font-semibold mb-2 text-gray-900 dark:text-gray-100">Rating Distribution</h3>
                    {!hasRatings ? (
                        <div className="text-sm text-muted-foreground py-8 text-center">No published ratings yet.</div>
                    ) : (
                        <div className="h-64">
                            <Bar data={ratingData} options={ratingOptions} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default HRGovernanceSummary;
